import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './CakeCheckout.css';


const CAKE_REFERENCE_DB = 'vakra_cake_reference_db';
const CAKE_REFERENCE_STORE = 'reference_images';

const openCakeReferenceDb = () =>
  new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('Reference image storage is not supported on this device.'));
      return;
    }

    const request = window.indexedDB.open(CAKE_REFERENCE_DB, 1);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(CAKE_REFERENCE_STORE)) {
        db.createObjectStore(CAKE_REFERENCE_STORE);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error || new Error('Unable to open reference image storage.'));
  });

const readCakeReferenceFile = async (key) => {
  if (!key) return null;

  const db = await openCakeReferenceDb();

  const value = await new Promise((resolve, reject) => {
    const transaction = db.transaction(CAKE_REFERENCE_STORE, 'readonly');
    const request = transaction.objectStore(CAKE_REFERENCE_STORE).get(key);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () =>
      reject(request.error || new Error('Reference image read failed.'));
  });

  db.close();

  if (!value?.blob) return null;

  return new File([value.blob], value.name || 'cake-reference.jpg', {
    type: value.type || value.blob.type || 'image/jpeg',
    lastModified: value.lastModified || Date.now(),
  });
};

const deleteCakeReferenceFile = async (key) => {
  if (!key || !window.indexedDB) return;

  try {
    const db = await openCakeReferenceDb();

    await new Promise((resolve, reject) => {
      const transaction = db.transaction(CAKE_REFERENCE_STORE, 'readwrite');
      transaction.objectStore(CAKE_REFERENCE_STORE).delete(key);
      transaction.oncomplete = resolve;
      transaction.onerror = () =>
        reject(transaction.error || new Error('Reference image cleanup failed.'));
    });

    db.close();
  } catch (error) {
    console.warn('Reference image cleanup failed:', error);
  }
};

const CakeCheckout = () => {
  const { shopKey = '' } = useParams();
  const navigate = useNavigate();

  const [shop, setShop] = useState(null);
  const [settings, setSettings] = useState(null);
  const [cart, setCart] = useState([]);

  const [step, setStep] = useState(1);
  const [referenceFile, setReferenceFile] = useState(null);
  const [referencePreview, setReferencePreview] = useState('');
  const [referenceSourceKey, setReferenceSourceKey] = useState('');
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);

  const [form, setForm] = useState({
    customer_name: '',
    customer_mobile: '',
    fulfilment_type: 'delivery',
    delivery_address: '',
    landmark: '',
    pincode: '',
    preferred_date: '',
    preferred_time_slot: '',
    payment_method: 'cod',
    customer_note: '',
  });

  const cartKey = `vakra_cake_cart_${String(shopKey).trim().toLowerCase()}`;

  const money = (value) =>
    `₹${Number(value || 0).toLocaleString('en-IN')}`;

  const formatTime = (value) => {
    const text = String(value || '').slice(0, 5);
    if (!text) return '';

    const [hourText, minute = '00'] = text.split(':');
    let hour = Number(hourText);
    if (!Number.isFinite(hour)) return text;

    const suffix = hour >= 12 ? 'PM' : 'AM';
    hour %= 12;
    if (hour === 0) hour = 12;

    return `${hour}:${minute} ${suffix}`;
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem(cartKey);
      const parsed = saved ? JSON.parse(saved) : [];
      setCart(Array.isArray(parsed) ? parsed : []);
    } catch (_) {
      setCart([]);
    }
  }, [cartKey]);

  useEffect(() => {
    const restoreReferenceImage = async () => {
      const customItem = cart.find(
        (item) => item?.customization?.reference_image_key
      );

      const referenceKey =
        customItem?.customization?.reference_image_key || '';

      if (!referenceKey || referenceFile) return;

      try {
        setReferenceLoading(true);

        const file = await readCakeReferenceFile(referenceKey);

        if (!file) return;

        setReferenceSourceKey(referenceKey);
        setReferenceFile(file);
        setReferencePreview(URL.createObjectURL(file));
      } catch (restoreError) {
        console.warn('Reference image restore failed:', restoreError);
      } finally {
        setReferenceLoading(false);
      }
    };

    restoreReferenceImage();
  }, [cart, referenceFile]);

  useEffect(() => {
    const load = async () => {
      try {
        const [shopResponse, settingsResponse] = await Promise.all([
          fetch(
            `/api/cake/products/public-shop/${encodeURIComponent(shopKey)}`
          ),
          fetch(
            `/api/cake/products/public-shop/${encodeURIComponent(
              shopKey
            )}/settings`
          ),
        ]);

        const shopData = await shopResponse.json();
        const settingsData = await settingsResponse
          .json()
          .catch(() => ({}));

        if (!shopResponse.ok) {
          throw new Error(shopData.message || 'Shop not found');
        }

        setShop(shopData.shop || null);

        if (settingsResponse.ok) {
          setSettings(settingsData.settings || null);

          const delivery = Number(settingsData.settings?.delivery_enabled);
          const pickup = Number(settingsData.settings?.pickup_enabled);

          setForm((prev) => ({
            ...prev,
            fulfilment_type: delivery
              ? 'delivery'
              : pickup
              ? 'pickup'
              : 'delivery',
          }));
        }
      } catch (loadError) {
        setError(loadError.message || 'Checkout load nahi hua');
      }
    };

    load();
  }, [shopKey]);

  useEffect(() => {
    return () => {
      if (referencePreview && referencePreview.startsWith('blob:')) {
        URL.revokeObjectURL(referencePreview);
      }
    };
  }, [referencePreview]);

  const cartTotal = useMemo(
    () =>
      cart.reduce(
        (sum, item) =>
          sum +
          Number(item.price || 0) *
            Math.max(1, Number(item.qty || 1)),
        0
      ),
    [cart]
  );

  const hasCustomCake = useMemo(
    () =>
      cart.some(
        (item) =>
          item.customization ||
          item.is_custom ||
          item.reference_image_name
      ),
    [cart]
  );

  const minimumDate = useMemo(() => {
    const date = new Date();

    if (hasCustomCake) {
      const hours = Math.max(
        0,
        Number(settings?.custom_advance_hours || 24)
      );
      date.setHours(date.getHours() + hours);
    }

    return date.toISOString().slice(0, 10);
  }, [hasCustomCake, settings]);

  const slots = useMemo(() => {
    const start = hasCustomCake
      ? settings?.custom_delivery_start_time
      : settings?.delivery_start_time;

    const end = hasCustomCake
      ? settings?.custom_delivery_end_time
      : settings?.delivery_end_time;

    if (!start || !end) {
      return [
        '10:00 AM - 12:00 PM',
        '12:00 PM - 2:00 PM',
        '2:00 PM - 4:00 PM',
        '4:00 PM - 6:00 PM',
        '6:00 PM - 8:00 PM',
      ];
    }

    return [`${formatTime(start)} - ${formatTime(end)}`];
  }, [hasCustomCake, settings]);

  const setField = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateStep = () => {
    setError('');

    if (step === 1) {
      if (!form.customer_name.trim()) {
        setError('Customer name required hai.');
        return false;
      }

      if (!form.customer_mobile.trim()) {
        setError('Mobile number required hai.');
        return false;
      }

      if (
        form.fulfilment_type === 'delivery' &&
        !form.delivery_address.trim()
      ) {
        setError('Delivery address required hai.');
        return false;
      }
    }

    if (step === 2) {
      if (!form.preferred_date) {
        setError('Date select karein.');
        return false;
      }

      if (!form.preferred_time_slot) {
        setError('Time slot select karein.');
        return false;
      }
    }

    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((current) => Math.min(3, current + 1));
  };

  const handleReference = (file) => {
    if (!file) {
      setReferenceFile(null);
      setReferencePreview('');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Reference image valid image file honi chahiye.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Reference image 5 MB se kam honi chahiye.');
      return;
    }

    if (referencePreview && referencePreview.startsWith('blob:')) {
      URL.revokeObjectURL(referencePreview);
    }

    if (referenceSourceKey) {
      deleteCakeReferenceFile(referenceSourceKey);
      setReferenceSourceKey('');
    }

    setReferenceFile(file);
    setReferencePreview(URL.createObjectURL(file));
    setError('');
  };

  const placeOrder = async () => {
    if (!validateStep()) return;

    try {
      setPlacing(true);
      setError('');

      const items = cart.map((item) => ({
        type: item.type === 'other' ? 'other' : 'cake',
        id: item.id,
        name: item.name,
        price: Number(item.price || 0),
        qty: Math.max(1, Number(item.qty || 1)),
        variant: item.variant || item.unit || '',
        flavor: item.flavor || '',
        is_custom: Boolean(item.customization),
        customization: item.customization
          ? {
              ...item.customization,
              cake_message:
                item.cake_message ||
                item.customization?.cake_message ||
                '',
              special_request:
                item.special_request ||
                item.customization?.special_request ||
                '',
              requested_date: item.requested_date || '',
              requested_time: item.requested_time || '',
              fulfilment_type: item.fulfilment_type || '',
              reference_image_name:
                item.customization?.reference_image_name ||
                referenceFile?.name ||
                '',
              reference_image_requested: Boolean(referenceFile),
            }
          : null,
      }));

      const orderPayload = {
        ...form,
        items,
        delivery_charge: 0,
      };

      const payload = new FormData();
      payload.append('order_json', JSON.stringify(orderPayload));

      if (referenceFile && hasCustomCake) {
        payload.append('reference_image', referenceFile);
      }

      const response = await fetch(
        `/api/cake/products/public-shop/${encodeURIComponent(
          shopKey
        )}/orders`,
        {
          method: 'POST',
          body: payload,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Order place nahi hua');
      }

      const referenceKeys = cart
        .map((item) => item?.customization?.reference_image_key)
        .filter(Boolean);

      await Promise.all(
        referenceKeys.map((key) => deleteCakeReferenceFile(key))
      );

      localStorage.removeItem(cartKey);
      setCart([]);
      setReferenceSourceKey('');
      setReferenceFile(null);
      setReferencePreview('');
      setOrder(data.order || null);
    } catch (orderError) {
      setError(orderError.message || 'Order place nahi hua');
    } finally {
      setPlacing(false);
    }
  };

  if (!cart.length && !order) {
    return (
      <div className="cake-checkout-empty">
        <span>🛒</span>
        <h2>Your cart is empty</h2>
        <button onClick={() => navigate(`/cake/${shopKey}`)}>
          Back to Shop
        </button>
      </div>
    );
  }

  if (order) {
    return (
      <div className="cake-checkout-success-page">
        <div className="cake-checkout-success-card">
          <div className="success-icon">✓</div>
          <small>ORDER CONFIRMED</small>
          <h1>Your cake order is placed</h1>
          <p>
            Vendor ko order mil gaya hai. Order accept hone ke baad
            preparation start hogi.
          </p>

          <div className="order-code">
            <span>Order ID</span>
            <strong>#{order.order_code}</strong>
          </div>

          {order.order_type === 'custom' && (
            <div className="custom-note">
              ✨ Custom cake details vendor order panel me available hain.
            </div>
          )}

          <button onClick={() => navigate(`/cake/${shopKey}`)}>
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cake-checkout-page">
      <header className="cake-checkout-topbar">
        <button
          onClick={() =>
            step === 1
              ? navigate(`/cake/${shopKey}`)
              : setStep((current) => current - 1)
          }
        >
          ←
        </button>

        <div>
          <strong>Checkout</strong>
          <small>{shop?.shop_name || 'Cake Shop'}</small>
        </div>

        <span>
          {step}/3
        </span>
      </header>

      <div className="cake-checkout-shell">
        <section className="cake-checkout-progress">
          {[1, 2, 3].map((value) => (
            <span
              key={value}
              className={step >= value ? 'active' : ''}
            />
          ))}
        </section>

        {error && <div className="cake-checkout-alert">{error}</div>}

        <div className="cake-checkout-layout">
          <main className="cake-checkout-main">
            {step === 1 && (
              <section className="cake-checkout-card">
                <span className="eyebrow">STEP 1</span>
                <h1>Customer & Delivery Details</h1>
                <p>
                  Order kis naam se aur delivery/pickup kahan chahiye.
                </p>

                <div className="cake-checkout-form-grid">
                  <label>
                    Full Name
                    <input
                      value={form.customer_name}
                      onChange={(e) =>
                        setField('customer_name', e.target.value)
                      }
                      placeholder="Customer name"
                    />
                  </label>

                  <label>
                    Mobile Number
                    <input
                      inputMode="tel"
                      value={form.customer_mobile}
                      onChange={(e) =>
                        setField('customer_mobile', e.target.value)
                      }
                      placeholder="10 digit mobile"
                    />
                  </label>
                </div>

                <div className="cake-checkout-choice">
                  {(!settings ||
                    Number(settings.delivery_enabled)) && (
                    <button
                      className={
                        form.fulfilment_type === 'delivery'
                          ? 'active'
                          : ''
                      }
                      onClick={() =>
                        setField('fulfilment_type', 'delivery')
                      }
                    >
                      🛵 Delivery
                    </button>
                  )}

                  {(!settings ||
                    Number(settings.pickup_enabled)) && (
                    <button
                      className={
                        form.fulfilment_type === 'pickup'
                          ? 'active'
                          : ''
                      }
                      onClick={() =>
                        setField('fulfilment_type', 'pickup')
                      }
                    >
                      🏪 Pickup
                    </button>
                  )}
                </div>

                {form.fulfilment_type === 'delivery' && (
                  <>
                    <label className="cake-checkout-label">
                      Delivery Address
                      <textarea
                        rows="3"
                        value={form.delivery_address}
                        onChange={(e) =>
                          setField(
                            'delivery_address',
                            e.target.value
                          )
                        }
                        placeholder="Flat/house, building, street, area..."
                      />
                    </label>

                    <div className="cake-checkout-form-grid">
                      <label>
                        Landmark
                        <input
                          value={form.landmark}
                          onChange={(e) =>
                            setField('landmark', e.target.value)
                          }
                        />
                      </label>

                      <label>
                        Pincode
                        <input
                          inputMode="numeric"
                          value={form.pincode}
                          onChange={(e) =>
                            setField('pincode', e.target.value)
                          }
                        />
                      </label>
                    </div>
                  </>
                )}
              </section>
            )}

            {step === 2 && (
              <section className="cake-checkout-card">
                <span className="eyebrow">STEP 2</span>
                <h1>Schedule Your Order</h1>
                <p>
                  Vendor ki configured timing ke hisab se date aur slot
                  select karein.
                </p>

                {hasCustomCake && (
                  <div className="cake-checkout-rule">
                    <span>🕒</span>
                    <div>
                      <strong>
                        Custom cake: minimum{' '}
                        {settings?.custom_advance_hours || 24} hours
                        advance
                      </strong>
                      <small>
                        Earliest available date automatically restricted.
                      </small>
                    </div>
                  </div>
                )}

                <label className="cake-checkout-label">
                  Preferred Date
                  <input
                    type="date"
                    min={minimumDate}
                    value={form.preferred_date}
                    onChange={(e) =>
                      setField('preferred_date', e.target.value)
                    }
                  />
                </label>

                <div className="cake-checkout-slot-grid">
                  {slots.map((slot) => (
                    <button
                      key={slot}
                      className={
                        form.preferred_time_slot === slot
                          ? 'active'
                          : ''
                      }
                      onClick={() =>
                        setField('preferred_time_slot', slot)
                      }
                    >
                      {slot}
                    </button>
                  ))}
                </div>

                {hasCustomCake && (
                  <div className="cake-checkout-reference-wrap">
                    <label className="cake-checkout-reference">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) =>
                          handleReference(e.target.files?.[0] || null)
                        }
                      />

                      <div>
                        <strong>Reference Design Image</strong>
                        <small>
                          {referenceLoading
                            ? 'Loading selected image...'
                            : referencePreview
                            ? 'Image ready • tap to replace'
                            : 'Optional • JPG/PNG/WEBP • max 5 MB'}
                        </small>
                      </div>

                      {referencePreview ? (
                        <img
                          src={referencePreview}
                          alt="Reference"
                        />
                      ) : (
                        <span>＋ Upload</span>
                      )}
                    </label>

                    {referencePreview && (
                      <div className="cake-checkout-reference-status">
                        <span>✓ Customer reference image attached</span>
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              referencePreview &&
                              referencePreview.startsWith('blob:')
                            ) {
                              URL.revokeObjectURL(referencePreview);
                            }

                            if (referenceSourceKey) {
                              deleteCakeReferenceFile(referenceSourceKey);
                            }

                            setReferenceSourceKey('');
                            setReferenceFile(null);
                            setReferencePreview('');
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <label className="cake-checkout-label">
                  Order Note
                  <textarea
                    rows="3"
                    value={form.customer_note}
                    onChange={(e) =>
                      setField('customer_note', e.target.value)
                    }
                    placeholder="Any preparation or delivery note..."
                  />
                </label>
              </section>
            )}

            {step === 3 && (
              <section className="cake-checkout-card">
                <span className="eyebrow">STEP 3</span>
                <h1>Review & Place Order</h1>
                <p>
                  Final details check karke order place karein.
                </p>

                <div className="cake-checkout-review-list">
                  {cart.map((item) => (
                    <article key={item.key}>
                      <div>
                        <strong>
                          {item.name} × {item.qty}
                        </strong>
                        <small>
                          {[item.variant, item.flavor, item.unit]
                            .filter(Boolean)
                            .join(' • ')}
                        </small>
                        {item.customization && (
                          <div className="cake-checkout-custom-review">
                            <span>✨ Custom Cake</span>

                            {item.customization.occasion && (
                              <small>
                                Occasion: {item.customization.occasion}
                              </small>
                            )}

                            {item.customization.recipient_name && (
                              <small>
                                Name on cake: {item.customization.recipient_name}
                              </small>
                            )}

                            {item.cake_message && (
                              <small>
                                Cake message: {item.cake_message}
                              </small>
                            )}

                            {item.special_request && (
                              <small>
                                Special request: {item.special_request}
                              </small>
                            )}

                            {(item.customization.reference_image_name ||
                              referencePreview) && (
                              <small>Reference image: Attached</small>
                            )}
                          </div>
                        )}
                      </div>

                      <strong>
                        {money(
                          Number(item.price || 0) *
                            Number(item.qty || 1)
                        )}
                      </strong>
                    </article>
                  ))}
                </div>

                <div className="cake-checkout-summary-lines">
                  <div>
                    <span>Customer</span>
                    <strong>
                      {form.customer_name} • {form.customer_mobile}
                    </strong>
                  </div>
                  <div>
                    <span>Mode</span>
                    <strong>
                      {form.fulfilment_type === 'pickup'
                        ? 'Pickup'
                        : 'Delivery'}
                    </strong>
                  </div>
                  <div>
                    <span>Date & Time</span>
                    <strong>
                      {form.preferred_date} •{' '}
                      {form.preferred_time_slot}
                    </strong>
                  </div>
                </div>

                <div className="cake-checkout-choice payment">
                  <button
                    className={
                      form.payment_method === 'cod' ? 'active' : ''
                    }
                    onClick={() =>
                      setField('payment_method', 'cod')
                    }
                  >
                    Cash on Delivery
                  </button>

                  {form.fulfilment_type === 'pickup' && (
                    <button
                      className={
                        form.payment_method === 'pay_at_shop'
                          ? 'active'
                          : ''
                      }
                      onClick={() =>
                        setField('payment_method', 'pay_at_shop')
                      }
                    >
                      Pay at Shop
                    </button>
                  )}
                </div>
              </section>
            )}
          </main>

          <aside className="cake-checkout-order-summary">
            <h2>Order Summary</h2>

            {cart.map((item) => (
              <div className="summary-item" key={item.key}>
                <span>
                  {item.name} × {item.qty}
                </span>
                <strong>
                  {money(
                    Number(item.price || 0) *
                      Number(item.qty || 1)
                  )}
                </strong>
              </div>
            ))}

            <div className="summary-total">
              <span>Total</span>
              <strong>{money(cartTotal)}</strong>
            </div>

            {hasCustomCake && settings && (
              <p>
                Custom order: minimum{' '}
                {settings.custom_advance_hours || 24} hours advance.
              </p>
            )}
          </aside>
        </div>

        <footer className="cake-checkout-footer">
          {step < 3 ? (
            <button onClick={next}>Continue</button>
          ) : (
            <button disabled={placing} onClick={placeOrder}>
              {placing
                ? 'Placing Order...'
                : `Place Order • ${money(cartTotal)}`}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default CakeCheckout;
